import { getLocale } from '@tov-plus/ui';
import { compareEventDisplayOrder, toGuestJourneyPath } from '@tov-plus/domain';
import { msg, useToast } from '@tov-plus/ui';
import { NavLink } from '@tov-plus/frontend-data';
import { useState } from 'react';

import { guestApi } from '../app/api';
import {
  buildGuestWorkflowSections,
  type GuestWorkflowStatus,
} from '../app/guestWorkflows';
import type { useGuestAccess } from '../contexts/GuestAccessContext';
import { useGuestLiveUpdates } from '../lib/communications/useGuestLiveUpdates';
import { getGuestErrorMessage } from '../lib/errors/guestErrors';
import type { GuestExperience } from '../lib/experience/guestExperience';
import { formatGuestDateTime } from '../lib/localization/guestLocale';
import { formatToken } from './guestHomePageSectionHelpers';

type ReadyGuestAccess = Extract<
  ReturnType<typeof useGuestAccess>,
  { status: 'ready' }
>;

function daysUntil(iso: string) {
  const now = new Date();
  const target = new Date(iso);
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.ceil((target.getTime() - now.getTime()) / msPerDay);
}

function checklistStatusLabel(status: GuestWorkflowStatus) {
  switch (status) {
    case 'complete':
      return msg('pages.guest_home_editorial.checklist_status_complete');
    case 'in_progress':
      return msg('pages.guest_home_editorial.checklist_status_in_progress');
    case 'not_started':
      return msg('pages.guest_home_editorial.checklist_status_not_started');
    case 'blocked':
      return msg('pages.guest_home_editorial.checklist_status_blocked');
  }
}

function escapeCalendarText(value: string) {
  return value
    .replaceAll('\\', '\\\\')
    .replaceAll(';', '\\;')
    .replaceAll(',', '\\,')
    .replaceAll('\n', '\\n');
}

function formatCalendarDate(value: string) {
  return new Date(value).toISOString().replaceAll('-', '').replaceAll(':', '');
}

function buildOccasionCalendarFile(experience: GuestExperience) {
  const events = [...experience.events]
    .filter((event) => event.startsAt)
    .sort(compareEventDisplayOrder);

  if (events.length === 0) {
    return null;
  }

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Tov Plus//Guest Website//EN',
    'CALSCALE:GREGORIAN',
    `X-WR-CALNAME:${escapeCalendarText(experience.occasion.displayName)}`,
    ...events.flatMap((event) => [
      'BEGIN:VEVENT',
      `UID:${escapeCalendarText(`${experience.occasion.id}-${event.id}@tov.plus`)}`,
      `DTSTAMP:${formatCalendarDate(new Date().toISOString())}`,
      `DTSTART:${formatCalendarDate(event.startsAt ?? event.createdAt)}`,
      event.endsAt ? `DTEND:${formatCalendarDate(event.endsAt)}` : null,
      `SUMMARY:${escapeCalendarText(event.displayName)}`,
      event.locationName || event.locationAddress
        ? `LOCATION:${escapeCalendarText(
            [event.locationName, event.locationAddress]
              .filter(Boolean)
              .join(', '),
          )}`
        : null,
      event.guestSummary || event.description
        ? `DESCRIPTION:${escapeCalendarText(
            event.guestSummary ?? event.description ?? '',
          )}`
        : null,
      'END:VEVENT',
    ]),
    'END:VCALENDAR',
  ].filter((line): line is string => line !== null);

  return `${lines.join('\r\n')}\r\n`;
}

function downloadTextFile(input: {
  contents: string;
  filename: string;
  mimeType: string;
}) {
  const blob = new Blob([input.contents], { type: input.mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = input.filename;
  link.click();
  window.URL.revokeObjectURL(url);
}

export function GuestHomeEditorial({
  experience,
  access,
}: {
  experience: GuestExperience;
  access: ReadyGuestAccess;
}) {
  const notifications = useToast();
  const liveUpdates = useGuestLiveUpdates(access);
  const [updatingInsightKey, setUpdatingInsightKey] = useState<string | null>(
    null,
  );

  const localeCode = getLocale();
  const timezone = experience.occasion.timezone;
  const firstName =
    experience.guestDisplayName.trim().split(/\s+/)[0] ||
    experience.guestDisplayName;
  const siteTitle =
    experience.guestWebsite.identity.site_title_override ??
    experience.occasion.displayName;
  const tagline = experience.guestWebsite.identity.subtitle_tagline;

  const upcomingEvents = [...experience.events]
    .filter((event) => event.startsAt)
    .sort(compareEventDisplayOrder);
  const nextEvent = upcomingEvents[0] ?? null;
  const daysRemaining = nextEvent?.startsAt
    ? daysUntil(nextEvent.startsAt)
    : null;

  const recommendedPath = toGuestJourneyPath(
    experience.journeySummary.recommendedTarget,
  );
  const pendingParts = [
    experience.journeySummary.pendingRsvpEventCount > 0
      ? `${experience.journeySummary.pendingRsvpEventCount} ${msg('pages.guest_home_page_sections.event_response_pending')}`
      : null,
    experience.journeySummary.pendingFormCount > 0
      ? `${experience.journeySummary.pendingFormCount} ${msg('pages.guest_home_page_sections.form_pending')}`
      : null,
  ].filter((part): part is string => part !== null);
  const hasPendingAction = pendingParts.length > 0;
  const placeCardBody = hasPendingAction
    ? pendingParts.join(' · ')
    : msg('pages.guest_home_editorial.place_card_confirmed_body');

  const workflowSections = buildGuestWorkflowSections(experience);
  const requiredTasks = experience.tasks.filter((task) => task.required);

  const intelligence = experience.guestIntelligence;
  const topInsight = intelligence?.insights[0] ?? null;
  const postEventGalleryPrompt =
    experience.guestWebsite.personalized_states
      .post_event_gallery_mode_enabled &&
    experience.journeySummary.availableTargets.includes('gallery');
  const hasUpdates =
    topInsight !== null ||
    experience.recentCommunications.length > 0 ||
    postEventGalleryPrompt;

  async function updateGuestInsightState(
    insightKey: string,
    state: 'acknowledged' | 'dismissed',
  ) {
    const occasionSlug = access.occasionSlug;
    const guestToken = access.guestToken;
    if (!occasionSlug || !guestToken) {
      return;
    }

    setUpdatingInsightKey(insightKey);
    try {
      await guestApi.updateGuestIntelligenceInsightState(
        occasionSlug,
        guestToken,
        insightKey,
        state,
      );
      notifications.success(
        state === 'dismissed'
          ? msg('pages.home_page.pages_home_page_intelligence_dismissed')
          : msg('pages.home_page.pages_home_page_intelligence_acknowledged'),
      );
      access.refresh();
    } catch (error) {
      notifications.error(
        getGuestErrorMessage(
          error,
          msg('pages.home_page.pages_home_page_intelligence_update_failed'),
        ),
      );
    } finally {
      setUpdatingInsightKey(null);
    }
  }

  const eventsWithLocations = experience.events.filter(
    (event) => event.locationName || event.locationAddress,
  );
  const firstAddress =
    eventsWithLocations.find((event) => event.locationAddress)
      ?.locationAddress ?? null;
  const mapHref = firstAddress
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        firstAddress,
      )}`
    : null;
  const calendarFile = buildOccasionCalendarFile(experience);

  function downloadCalendar() {
    if (!calendarFile) {
      notifications.info(
        msg('pages.guest_home_page_sections.calendar_times_not_ready_yet'),
      );
      return;
    }

    downloadTextFile({
      contents: calendarFile,
      filename: `${experience.occasion.slug}-guest-schedule.ics`,
      mimeType: 'text/calendar;charset=utf-8',
    });
    notifications.success(
      msg('pages.guest_home_page_sections.calendar_file_ready'),
    );
  }

  async function shareInvite() {
    const visibleEventLines = upcomingEvents.slice(0, 5).map((event) => {
      const time = event.startsAt
        ? formatGuestDateTime(event.startsAt, localeCode, timezone)
        : msg('pages.guest_home_page_sections.time_to_be_announced');
      return `${event.displayName} - ${time}`;
    });
    const shareText = [
      experience.occasion.displayName,
      msg('pages.guest_home_page_sections.household_share_intro'),
      ...visibleEventLines,
      msg('pages.guest_home_page_sections.household_share_access_guidance'),
    ].join('\n');
    const shareData = {
      title: experience.occasion.displayName,
      text: shareText,
      url: window.location.href,
    };

    if (navigator.share) {
      await navigator.share(shareData);
      return;
    }

    await navigator.clipboard.writeText(
      `${shareText}\n${window.location.href}`,
    );
    notifications.success(
      msg('pages.guest_home_page_sections.invite_link_copied'),
    );
  }

  return (
    <div className="guest-landing guest-home-editorial">
      <section className="guest-landing__hero">
        <p className="guest-landing__eyebrow">
          {msg('pages.guest_home_editorial.hero_eyebrow')}, {firstName}
        </p>
        <h1>{siteTitle}</h1>
        {tagline ? <p className="guest-landing__subline">{tagline}</p> : null}
        {nextEvent?.startsAt ? (
          <p className="guest-landing__details">
            {formatGuestDateTime(nextEvent.startsAt, localeCode, timezone)}
            {nextEvent.locationName ? ` — ${nextEvent.locationName}` : ''}
          </p>
        ) : null}
        <p className="guest-landing__tagline">
          {experience.journeySummary.summary}
        </p>
        <div className="guest-landing__btn-row">
          <NavLink
            className="guest-landing__btn guest-landing__btn--primary"
            to={recommendedPath}
          >
            {experience.journeySummary.recommendedLabel}
          </NavLink>
          {access.resumePath ? (
            <NavLink
              className="guest-landing__btn guest-landing__btn--outline"
              to={access.resumePath}
            >
              {msg(
                'pages.guest_home_page_sections.pages_guest_home_page_sections_resume_where_you_left_off',
              )}
            </NavLink>
          ) : null}
        </div>
      </section>

      {daysRemaining !== null && daysRemaining >= 0 ? (
        <div className="guest-landing__countdown">
          <b>{daysRemaining}</b>{' '}
          {daysRemaining === 1
            ? msg('pages.guest_landing_page.countdown_day')
            : msg('pages.guest_landing_page.countdown_days')}{' '}
          {msg('pages.guest_landing_page.countdown_until_the_celebration')}
        </div>
      ) : null}

      <section className="guest-landing__rsvp guest-home-editorial__status">
        <p className="guest-landing__eyebrow">
          {msg('pages.guest_home_editorial.place_card_eyebrow')}
        </p>
        <h2>
          {hasPendingAction
            ? msg('pages.guest_home_editorial.place_card_pending_title')
            : msg('pages.guest_home_editorial.place_card_confirmed_title')}
        </h2>
        <p>{placeCardBody}</p>
        <NavLink
          className="guest-landing__btn guest-landing__btn--primary"
          to={recommendedPath}
        >
          {experience.journeySummary.recommendedLabel}
        </NavLink>
      </section>

      {upcomingEvents.length > 0 ? (
        <section className="guest-landing__events">
          <p className="guest-landing__eyebrow">
            {msg('pages.guest_home_page_sections.your_timeline')}
          </p>
          <div className="guest-landing__events-grid">
            {upcomingEvents.slice(0, 4).map((event) => (
              <article className="guest-landing__event-card" key={event.id}>
                <p className="guest-landing__event-tag">
                  {event.startsAt
                    ? formatGuestDateTime(
                        event.startsAt,
                        localeCode,
                        event.timezone ?? timezone,
                      )
                    : msg(
                        'pages.guest_home_page_sections.time_to_be_announced',
                      )}
                </p>
                <h3>{event.displayName}</h3>
                {event.locationName || event.locationAddress ? (
                  <p className="guest-landing__event-meta">
                    {[event.locationName, event.locationAddress]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                ) : null}
                {event.guestSummary ? (
                  <p className="guest-landing__event-meta">
                    {event.guestSummary}
                  </p>
                ) : null}
              </article>
            ))}
          </div>
          <div className="guest-landing__more-link">
            <NavLink to="/events">
              {msg('pages.guest_home_page_sections.all_event_details')}
            </NavLink>
          </div>
        </section>
      ) : null}

      <section className="guest-home-editorial__checklist">
        <p className="guest-landing__eyebrow">
          {msg('pages.guest_home_page_sections.what_needs_attention')}
        </p>
        <div className="guest-home-editorial__checklist-list">
          {workflowSections.map((section, index) => (
            <NavLink
              key={section.id}
              className="guest-home-editorial__checklist-row"
              to={section.primaryAction.href}
            >
              <span
                aria-hidden="true"
                className="guest-home-editorial__checklist-index"
              >
                {String(index + 1).padStart(2, '0')}
              </span>
              <span className="guest-home-editorial__checklist-body">
                <strong>{section.title}</strong>
                <span>{section.summary}</span>
              </span>
              <span
                className={`guest-home-editorial__checklist-status guest-home-editorial__checklist-status--${section.status}`}
              >
                {checklistStatusLabel(section.status)}
              </span>
            </NavLink>
          ))}
        </div>
        {requiredTasks.length > 0 ? (
          <>
            <p className="guest-home-editorial__checklist-subhead">
              {msg('pages.guest_home_editorial.checklist_tasks_subheading')}
            </p>
            <div className="guest-home-editorial__checklist-list">
              {requiredTasks.map((task, index) => (
                <div
                  className="guest-home-editorial__checklist-row"
                  key={task.type}
                >
                  <span
                    aria-hidden="true"
                    className="guest-home-editorial__checklist-index"
                  >
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="guest-home-editorial__checklist-body">
                    <strong>{task.label}</strong>
                    {task.metricLabel && task.metricValue ? (
                      <span>
                        {task.metricLabel}: {task.metricValue}
                      </span>
                    ) : null}
                  </span>
                  <span
                    className={`guest-home-editorial__checklist-status${
                      task.status === 'complete'
                        ? ' guest-home-editorial__checklist-status--complete'
                        : ''
                    }`}
                  >
                    {formatToken(task.status)}
                  </span>
                </div>
              ))}
            </div>
          </>
        ) : null}
      </section>

      <section className="guest-home-editorial__updates">
        <p className="guest-landing__eyebrow">
          {msg(
            'pages.guest_home_page_sections.pages_guest_home_page_sections_recent_messages',
          )}
          {liveUpdates.unreadCount > 0 ? ` (${liveUpdates.unreadCount})` : ''}
        </p>
        {hasUpdates ? (
          <div className="guest-home-editorial__updates-list">
            {topInsight ? (
              <article className="guest-home-editorial__update-row">
                <p className="guest-home-editorial__update-tag">
                  {topInsight.title}
                </p>
                <p className="guest-home-editorial__update-title">
                  {topInsight.recommendedAction}
                </p>
                <p className="guest-home-editorial__update-body">
                  {topInsight.explanation}
                </p>
                <div className="guest-home-editorial__update-actions">
                  <NavLink
                    className="guest-landing__btn guest-landing__btn--outline"
                    to={topInsight.href}
                  >
                    {msg('general.actions.open')}
                  </NavLink>
                  {topInsight.userState === 'acknowledged' ? null : (
                    <button
                      disabled={updatingInsightKey === topInsight.key}
                      onClick={() =>
                        void updateGuestInsightState(
                          topInsight.key,
                          'acknowledged',
                        )
                      }
                      type="button"
                    >
                      {msg('general.actions.acknowledge')}
                    </button>
                  )}
                  <button
                    disabled={updatingInsightKey === topInsight.key}
                    onClick={() =>
                      void updateGuestInsightState(topInsight.key, 'dismissed')
                    }
                    type="button"
                  >
                    {msg('general.actions.dismiss')}
                  </button>
                </div>
              </article>
            ) : null}
            {postEventGalleryPrompt ? (
              <article className="guest-home-editorial__update-row">
                <p className="guest-home-editorial__update-tag">
                  {msg('pages.home_page.pages_home_page_post_event_follow_up')}
                </p>
                <p className="guest-home-editorial__update-title">
                  {msg(
                    'pages.home_page.pages_home_page_keep_the_occasion_open_for_thank_yous_and',
                  )}
                </p>
                <div className="guest-home-editorial__update-actions">
                  <NavLink
                    className="guest-landing__btn guest-landing__btn--outline"
                    to="/follow-up"
                  >
                    {msg('pages.home_page.pages_home_page_open_follow_up_page')}
                  </NavLink>
                </div>
              </article>
            ) : null}
            {experience.recentCommunications.slice(0, 4).map((item) => (
              <article
                className="guest-home-editorial__update-row"
                key={item.delivery.id}
              >
                <p className="guest-home-editorial__update-tag">
                  {formatToken(item.delivery.status)}
                </p>
                <p className="guest-home-editorial__update-title">
                  {item.message?.subjectText ??
                    item.event?.displayName ??
                    msg('pages.help_page.pages_help_page_occasion_update')}
                </p>
              </article>
            ))}
          </div>
        ) : (
          <p className="guest-home-editorial__empty">
            {msg(
              'pages.guest_home_page_sections.pages_guest_home_page_sections_important_planner_messages_will_appear_here_when',
            )}
          </p>
        )}
        <div className="guest-landing__more-link">
          <NavLink to="/messages">
            {msg(
              'pages.guest_home_page_sections.pages_guest_home_page_sections_review_updates',
            )}
          </NavLink>
        </div>
      </section>

      <section className="guest-home-editorial__logistics">
        <p className="guest-landing__eyebrow">
          {msg('pages.guest_landing_page.travel_eyebrow')}
        </p>
        <div className="guest-home-editorial__logistics-grid">
          <NavLink
            className="guest-home-editorial__logistics-item"
            to="/locations"
          >
            <strong>{msg('pages.guest_home_page_sections.open_maps')}</strong>
            <span>
              {eventsWithLocations.length}{' '}
              {msg('pages.guest_home_page_sections.location_detail')}
            </span>
          </NavLink>
          <button
            className="guest-home-editorial__logistics-item"
            onClick={downloadCalendar}
            type="button"
          >
            <strong>
              {msg('pages.guest_home_page_sections.add_to_calendar')}
            </strong>
            <span>
              {msg('pages.guest_home_page_sections.download_calendar_file')}
            </span>
          </button>
          <button
            className="guest-home-editorial__logistics-item"
            onClick={() => void shareInvite()}
            type="button"
          >
            <strong>
              {msg('pages.guest_home_page_sections.share_with_household')}
            </strong>
            <span>
              {msg('pages.guest_home_page_sections.share_or_copy_invite_link')}
            </span>
          </button>
          <NavLink
            className="guest-home-editorial__logistics-item"
            to="/travel-packet"
          >
            <strong>
              {msg('pages.guest_home_page_sections.offline_travel_packet')}
            </strong>
            <span>
              {msg('pages.guest_home_page_sections.saved_day_of_logistics')}
            </span>
          </NavLink>
          <NavLink className="guest-home-editorial__logistics-item" to="/help">
            <strong>{msg('app.navigation.help')}</strong>
            <span>{msg('pages.guest_home_page_sections.contact_and_faq')}</span>
          </NavLink>
          {mapHref ? (
            <a className="guest-home-editorial__logistics-item" href={mapHref}>
              <strong>
                {msg('pages.guest_home_page_sections.open_first_address')}
              </strong>
              <span>{firstAddress}</span>
            </a>
          ) : null}
        </div>
      </section>

      <footer className="guest-landing__footer">
        <p className="guest-landing__sign">{siteTitle}</p>
        <div className="guest-home-editorial__footer-links">
          <NavLink className="guest-landing__fine" to="/access">
            {msg('pages.guest_home_editorial.footer_manage_access')}
          </NavLink>
          <NavLink className="guest-landing__fine" to="/help">
            {msg('app.navigation.help')}
          </NavLink>
        </div>
        <p className="guest-landing__powered">
          {msg('pages.guest_landing_page.footer_tagline')}
        </p>
      </footer>
    </div>
  );
}
